import React, { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import _cloneDeep from "lodash/cloneDeep";
import { useFetchData } from "app/hooks/hooks";
import { dashboardTestFlowResultsGet } from "app/api/v2/dashboardApi";
import { ChartColorsEnum } from "app/constants/dashboardConst";
import { formatChartDate } from "app/helpers/dashboardHelper";
import { DashboardItemFilterComplete } from "app/types/dashboardType";
import DashboardItemLayout from "../DashboardItemLayout/DashboardItemLayout";
import DashboardChart, {
  ChartData,
  ChartDataSets,
} from "../DashboardChart/DashboardChart";
import {
  DashboardApiV2DashboardTestFlowResultsGetRequest,
  DashboardTestFlowResults,
} from "@generated/v2";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import { SwitchProps } from "app/components/Common/Switch";
import { ChartTestFlowToggleLabel } from "app/screens/DashboardScreen/components/common/ChartTestFlowToggleLabel";

const ChartTestResults = ({
  customerId,
  siteId,
  start,
  end,
  routes,
  period,
}: DashboardItemFilterComplete) => {
  const { t } = useTranslation();

  const [showPositive, setShowPositive] = useState(true);
  const [showNegative, setShowNegative] = useState(true);

  const { data, loading } = useFetchData<
    DashboardTestFlowResults,
    DashboardApiV2DashboardTestFlowResultsGetRequest
  >(dashboardTestFlowResultsGet, {
    shouldCallApi: !!customerId,
    params: {
      customerId,
      siteId,
      start,
      end,
      routes,
    },
  });

  const chartData = useMemo<ChartData>(() => {
    const dataClone = _cloneDeep(data);
    let datasets: ChartDataSets[] = [];

    const negativeCount = {
      label: t("dashboard.datasetLabelNegative"),
      data: dataClone?.negativeCount ?? [],
      backgroundColor: ChartColorsEnum.BLUE,
      maxBarThickness: 24,
    };

    const positiveCount = {
      label: t("dashboard.datasetLabelPositive"),
      data: dataClone?.positiveCount ?? [],
      backgroundColor: ChartColorsEnum.RED,
      maxBarThickness: 24,
    };

    if (showNegative) {
      datasets.push(negativeCount);
    }

    if (showPositive) {
      datasets.push(positiveCount);
    }

    const labels = !!dataClone?.dates
      ? formatChartDate(dataClone?.dates, dataClone?.type)
      : [];

    return { datasets, labels };
  }, [data, showNegative, showPositive, t]);

  const togglePositive = useCallback(() => {
    setShowPositive(prevState => !prevState);
  }, []);

  const toggleNegative = useCallback(() => {
    setShowNegative(prevState => !prevState);
  }, []);

  const checkboxes = useMemo<SwitchProps[]>(
    () => [
      {
        checked: showPositive,
        onChange: togglePositive,
        label: (
          <ChartTestFlowToggleLabel
            i18nKey="dashboard.checkboxShowPositiveFlowsWithBox"
            positive
          />
        ),
      },
      {
        checked: showNegative,
        onChange: toggleNegative,
        label: (
          <ChartTestFlowToggleLabel
            i18nKey="dashboard.checkboxShowNegativeFlowsWithBox"
            positive={false}
          />
        ),
      },
    ],
    [showNegative, showPositive, toggleNegative, togglePositive]
  );

  return (
    <ErrorFallbackUI>
      <DashboardItemLayout
        background
        loading={loading}
        title={t("dashboard.testFlowResultsTitle")}
        subTitle={period}
        info={t("dashboard.testFlowResultsInfo")}
        switches={checkboxes}
      >
        <DashboardChart type="bar" stacked data={chartData} />
      </DashboardItemLayout>
    </ErrorFallbackUI>
  );
};

export default memo(ChartTestResults);
